import {
  localStorageActions,
  storageKeys
} from '../app/utils/localStorageActions';
import { Session, SessionImpl, SessionData } from './auth.model';
import { logger } from './logger';

const authUrlBase = '/api/auth';

// In-memory session
let session = new SessionImpl();

// Used to deduplicate concurrent token refreshes
let sessionPromise: Promise<Session> | null = null;

// TEST ONLY: Reset session state
export const cleanupForTest = () => {
  logger.debug(`session.cleanupForTest`);
  session = new SessionImpl();
  sessionPromise = null;
};

/**
 * Get the in-memory session.
 *
 * If the returned session is not valid, this indicates that the session has expired
 * and login is required to establish a new session.
 *
 * @returns the session
 */
export const getSession = (): Session => {
  logger.debug(`> auth.getSession`);
  if (session.data && !session.isValid()) {
    logger.debug(`auth.getSession: session invalid: ${session.toString()}`);
    session = session.set(null, session.sessionStoreStatus, null);
  }
  logger.debug(`< auth.getSession: ${session.toString()}`);
  return session;
};

export const forceClearSession = () => {
  logger.debug(` < auth.session: forceClearSession`);
  session = session.set(
    null, // no session data
    true, // sessionStoreStatus → "fetched"
    { code: 401, text: 'Unauthorized' } // error object
  );
  return session;
};

/**
 * Fetch an API authentication token using the /auth/session endpoint.
 *
 * If the calling session is valid, a fresh token should be returned.
 *
 * If a token is not returned, it indicates that the session has expired
 * and login is required to establish a new session.
 *
 * @returns session
 */
export const fetchSession = async (): Promise<Session> => {
  logger.debug(`> auth.fetchSession`);

  // If there's already a fetch in progress, return it
  if (sessionPromise) {
    logger.debug(`auth.fetchSession: sessionPromise already exists`);
    return sessionPromise;
  }

  sessionPromise = (async () => {
    try {
      logger.debug(`auth.fetchSession: await fetch`);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const resp: Response = await (window as any).originalFetch(
        `${authUrlBase}/session`,
        {
          credentials: 'include'
        }
      );

      logger.debug(
        `auth.fetchSession: response: ${resp.status}: ${resp.statusText}`
      );

      if (resp.ok) {
        const json = await resp.json();
        session = session.set(new SessionData(json), true, null);
      } else {
        const error = { code: resp.status, text: resp.statusText };
        session = session.set(null, resp.status === 401, error);
      }
    } catch (err) {
      logger.error(err);
      session = session.set(null, false, {
        code: -1,
        text: err instanceof Error ? err.message : String(err)
      });
    } finally {
      sessionPromise = null; // Clear the promise once done
    }

    logger.debug(
      `< auth.fetchSession: ${session}; valid? ${session.isValid()}`
    );
    return session;
  })();

  return sessionPromise;
};

export const deleteSession = () => {
  logger.debug(`auth: deleteSession`);
  session = session.set(null, session.sessionStoreStatus, null);
};

export const fetchLoginUrl = async (
  tenantId: string = ''
): Promise<Response> => {
  logger.debug(`> auth.fetchLoginUrl`);
  const lastLoggedTenantId =
    tenantId || localStorageActions.get(storageKeys.LAST_LOGIN_TENANT_ID);
  const pathUrl = lastLoggedTenantId ? `?ts_tid=${lastLoggedTenantId}` : '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).originalFetch(`${authUrlBase}/login/href${pathUrl}`);
};

// ///////////////////////////////////////////////////////////////////////////////
// // override the global window fetch function
// ///////////////////////////////////////////////////////////////////////////////

/**
 * Override the global window fetch function
 * to transparently add Authorization header tp API requests
 * from in-memory session
 */

// stash original fetch on window object so it can be used where required
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).originalFetch = window.fetch;

// Override fetch to inject Authorization header
// if response is 401, application will trigger login on next user navigation
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  logger.debug(`> auth.fetch`);
  let currentSession = getSession();

  // Only fetch a new session if token is invalid and session is valid
  if (currentSession.isValid() && !currentSession.isTokenValid()) {
    logger.debug(
      `auth.fetch: session valid, token invalid – fetching new token`
    );
    await fetchSession();
    currentSession = getSession();
  }

  // If still invalid, return 401 immediately
  if (!currentSession.isTokenValid()) {
    logger.log(`< auth.fetch: 401: ${currentSession.error()}`);
    return new Response(null, {
      status: 401,
      statusText: currentSession.error()
    });
  }

  // Prepare headers
  let headers: Headers;
  let isRequestInfo = false;
  if (init) {
    headers = new Headers(init?.headers || {});
  } else if (Object.prototype.hasOwnProperty.call(input, 'headers')) {
    isRequestInfo = true;
    headers = new Headers((input as Request)?.headers || {});
  } else {
    headers = new Headers({});
  }

  // Inject token
  logger.debug(`auth.fetch: set Authorization header`);
  headers.set('Authorization', `Bearer ${currentSession.getToken()}`);

  // Rebuild input/init with updated headers
  if (isRequestInfo) {
    input = { ...(input as Request), headers };
  } else {
    init = { ...(init || {}), headers };
  }
  logger.debug(`< auth.fetch`);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).originalFetch.apply(window, [input, init]);
};
